import { Box, Typography, Button, Grid, Paper, Chip } from '@mui/material';
import { useTheme } from '@mui/material';
import { ArrowBack, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useWorkSchedule } from '../context/useTimeClock';
import { WORK_SCHEDULE_TYPE_COLORS, WEEKLY_DAYS_OPTIONS } from '../constants/timeclockConstants';

export default function WorkScheduleDetailPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { workScheduleData, isDataLoading, navigateToEdit } = useWorkSchedule();

  const formatTime = (time: string) => {
    return format(new Date(`2000-01-01T${time}`), 'HH:mm');
  };

  const formatDateTime = (dateTime: string) => {
    return format(new Date(dateTime), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  const getTypeColor = (type: string) => {
    return WORK_SCHEDULE_TYPE_COLORS[type as keyof typeof WORK_SCHEDULE_TYPE_COLORS] || '#757575';
  };

  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      'REGULAR': 'Regular',
      'FLEXIBLE': 'Flexível',
      'PART_TIME': 'Meio Período',
      'OVERTIME': 'Horas Extras'
    };
    return typeMap[type] || type;
  };

  const formatWeeklyDays = (days: string[]) => {
    if (!days || days.length === 0) return 'Nenhum dia selecionado';
    
    const dayLabels = WEEKLY_DAYS_OPTIONS
      .filter(option => days.includes(option.value))
      .map(option => option.label);
    
    return dayLabels.join(', ');
  };

  if (isDataLoading) {
    return (
      <Box style={{ 
        background: theme.palette.background.paper, 
        borderRadius: 16, 
        padding: 32, 
        boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)', 
        maxWidth: '100%', 
        margin: '0 auto' 
      }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (!workScheduleData) {
    return (
      <Box style={{ 
        background: theme.palette.background.paper, 
        borderRadius: 16, 
        padding: 32, 
        boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)', 
        maxWidth: '100%', 
        margin: '0 auto' 
      }}>
        <Typography>Jornada não encontrada</Typography>
      </Box>
    );
  }
console.log(workScheduleData);
  return (
    <Box style={{ 
      background: theme.palette.background.paper, 
      borderRadius: 16, 
      padding: 32, 
      boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)', 
      maxWidth: '100%', 
      margin: '0 auto' 
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/timeclock')}
            variant="outlined"
          >
            Voltar
          </Button>
          <Typography variant="h4" color={theme.palette.text.primary}>
            Detalhes da Jornada
          </Typography>
        </Box>
        <Button
          startIcon={<Edit />}
          onClick={() => navigateToEdit(workScheduleData.id)}
          variant="contained"
        >
          Editar
        </Button>
      </Box>

      {/* Content */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom color={theme.palette.text.primary}>
              Informações Gerais
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                Nome da Jornada
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {workScheduleData.name}
              </Typography>

              <Typography variant="body2" color={theme.palette.text.secondary}>
                Tipo
              </Typography>
              <Chip
                label={getTypeLabel(workScheduleData.type)}
                size="small"
                sx={{
                  backgroundColor: getTypeColor(workScheduleData.type),
                  color: 'white',
                  fontWeight: 500,
                  mb: 2
                }}
              />

              {workScheduleData.description && (
                <>
                  <Typography variant="body2" color={theme.palette.text.secondary}>
                    Descrição
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {workScheduleData.description}
                  </Typography>
                </>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom color={theme.palette.text.primary}>
              Horários
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                Horário de Início
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatTime(workScheduleData.startTime)}
              </Typography>

              <Typography variant="body2" color={theme.palette.text.secondary}>
                Horário de Fim
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatTime(workScheduleData.endTime)}
              </Typography>

              <Typography variant="body2" color={theme.palette.text.secondary}>
                Dias da Semana
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatWeeklyDays(workScheduleData.weeklyDays)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color={theme.palette.text.primary}>
              Informações do Sistema
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color={theme.palette.text.secondary}>
                  Criado em
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(workScheduleData.createdAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color={theme.palette.text.secondary}>
                  Última atualização
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(workScheduleData.updatedAt)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
} 